/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Building2, ChevronDown, ChevronRight, Loader2, Check, LayoutGrid, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import toastService from '../utils/toastService';

const CLASS_TEXT_MUTED = 'text-base-content/70';
const CLASS_TEXT_PRIMARY = 'text-primary';

const AccountSwitcher = ({ isCollapsed }) => {
  const HOME_ROUTE = '/';

  const navigate = useNavigate();
  const { user, accountName, switchContext, resetContext } = useAuth();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const dropdownRef = useRef(null);

  // Platform admin state
  const [accounts, setAccounts] = useState([]);
  const [subAccounts, setSubAccounts] = useState({});
  const [expanded, setExpanded] = useState({});
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingSubs, setLoadingSubs] = useState({});

  // Account admin state
  const [ownSubAccounts, setOwnSubAccounts] = useState([]);
  const [loadingOwnSubs, setLoadingOwnSubs] = useState(false);

  const isPlatformAdmin = user?.scope === 'platform';
  const isAccountAdmin = user?.scope === 'account';

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Open automatically when an API call requires a sub-account context
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('sub-account-required', handler);
    return () => window.removeEventListener('sub-account-required', handler);
  }, []);

  // Platform admin: load all accounts when dropdown opens
  useEffect(() => {
    if (!open || !isPlatformAdmin) return;
    setLoadingAccounts(true);
    apiClient.get('/accounts', { params: { limit: 100, page: 1 } })
      .then(({ data }) => setAccounts(data.data ?? []))
      .catch(() => toastService.error('Failed to load accounts'))
      .finally(() => setLoadingAccounts(false));
  }, [open, isPlatformAdmin]);

  // Account admin: load their own sub-accounts when dropdown opens
  useEffect(() => {
    if (!open || !isAccountAdmin || !user?.accountUID) return;
    if (ownSubAccounts.length > 0) return; // already loaded
    setLoadingOwnSubs(true);
    apiClient.get(`/accounts/${user.accountUID}/sub-accounts`, { params: { limit: 100, page: 1 } })
      .then(({ data }) => setOwnSubAccounts(data.data ?? []))
      .catch(() => toastService.error('Failed to load sub-accounts'))
      .finally(() => setLoadingOwnSubs(false));
  }, [open, isAccountAdmin, user?.accountUID]);

  // Only render for roles that need context switching — after all hooks
  if (!isPlatformAdmin && !isAccountAdmin) return null;

  // Platform admin: lazy-load sub-accounts per account
  const fetchSubAccounts = async (accountId) => {
    if (subAccounts[accountId]) return;
    setLoadingSubs((s) => ({ ...s, [accountId]: true }));
    try {
      const { data } = await apiClient.get(`/accounts/${accountId}/sub-accounts`, { params: { limit: 100, page: 1 } });
      setSubAccounts((s) => ({ ...s, [accountId]: data.data ?? [] }));
    } catch {
      toastService.error('Failed to load sub-accounts');
    } finally {
      setLoadingSubs((s) => ({ ...s, [accountId]: false }));
    }
  };

  const toggleExpand = async (e, accountId) => {
    e.stopPropagation();
    const next = !expanded[accountId];
    setExpanded((s) => ({ ...s, [accountId]: next }));
    if (next) await fetchSubAccounts(accountId);
  };

  const handleResetContext = async () => {
    if (switching) return;
    setSwitching(true);
    setOpen(false);
    try {
      await resetContext();
      toastService.success(isPlatformAdmin ? 'Switched to all accounts' : 'Switched to all sub-accounts');
      navigate(HOME_ROUTE);
    } catch (err) {
      toastService.error(err?.response?.data?.message ?? 'Failed to reset context');
    } finally {
      setSwitching(false);
    }
  };

  const handleSwitchAccount = async (accountId) => {
    if (switching) return;
    setSwitching(true);
    setOpen(false);
    try {
      await switchContext(accountId);
      toastService.success('Switched account');
      navigate(HOME_ROUTE);
    } catch (err) {
      toastService.error(err?.response?.data?.message ?? 'Failed to switch account');
    } finally {
      setSwitching(false);
    }
  };

  const handleSwitchSubAccount = async (accountId, subAccountId, e) => {
    if (e) e.stopPropagation();
    if (switching) return;
    setSwitching(true);
    setOpen(false);
    try {
      await switchContext(accountId, subAccountId);
      toastService.success('Switched to sub-account');
      navigate(HOME_ROUTE);
    } catch (err) {
      toastService.error(err?.response?.data?.message ?? 'Failed to switch sub-account');
    } finally {
      setSwitching(false);
    }
  };

  // Button label
  const currentLabel = isPlatformAdmin
    ? (accountName ?? 'All Accounts')
    : (ownSubAccounts.find(s => s.id === user?.subAccountUID)?.name ?? (user?.subAccountUID ? user.subAccountUID : 'All Sub-accounts'));

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        title={isCollapsed ? currentLabel : undefined}
        className={`flex items-center gap-2 w-full px-3 py-2 rounded-xl bg-primary/10 hover:bg-primary/20 text-primary text-xs font-black uppercase tracking-widest transition-colors ${isCollapsed ? 'justify-center' : 'justify-between'}`}
      >
        {switching ? (
          <Loader2 size={16} className="animate-spin flex-shrink-0" />
        ) : (
          <Building2 size={16} className="flex-shrink-0" />
        )}
        {!isCollapsed && (
          <>
            <span className="flex-1 text-left truncate">{currentLabel}</span>
            <ChevronDown size={14} className={`flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
          </>
        )}
      </button>

      {open && (
        <div className="absolute bottom-full left-0 right-0 mb-2 w-100 bg-base-200 border border-base-content/10 rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-base-content/10">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-base-content/40">
              {isPlatformAdmin ? 'Switch Account' : 'Switch Sub-account'}
            </p>
          </div>

          <div className="max-h-72 overflow-y-auto">

            {/* ── PLATFORM ADMIN: two-level (accounts → sub-accounts) ── */}
            {isPlatformAdmin && (
              <>
                {/* All Accounts — reset to platform level */}
                <button
                  type="button"
                  onClick={handleResetContext}
                  className={`flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-base-content/5 transition-colors border-b border-base-content/5 ${user?.accountUID
                    ? CLASS_TEXT_MUTED
                    : CLASS_TEXT_PRIMARY
                    }`}
                >
                  <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Globe size={13} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-black truncate">All Accounts</p>
                    <p className="text-[10px] text-base-content/40 truncate uppercase tracking-wider">Platform view</p>
                  </div>
                  {!user?.accountUID && <Check size={13} className="text-primary flex-shrink-0" />}
                </button>

                {loadingAccounts ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 size={20} className="animate-spin text-primary" />
                  </div>
                ) : accounts.length === 0 ? (
                  <p className="text-base-content/40 text-xs text-center py-6 font-black uppercase">No accounts found</p>
                ) : (
                  accounts.map((acct) => {
                    const isActiveAccount = acct.id === user?.accountUID;
                    const isOpen = expanded[acct.id];
                    const subs = subAccounts[acct.id] ?? [];
                    const loadingSub = loadingSubs[acct.id];

                    return (
                      <div key={acct.id}>
                        <div
                          className={`flex items-center gap-2 px-3 py-2.5 hover:bg-base-content/5 transition-colors cursor-pointer group ${isActiveAccount ? CLASS_TEXT_PRIMARY : CLASS_TEXT_MUTED}`}
                          onClick={() => handleSwitchAccount(acct.id)}
                        >
                          <button
                            onClick={(e) => toggleExpand(e, acct.id)}
                            className="w-5 h-5 flex items-center justify-center rounded hover:bg-base-content/10 flex-shrink-0 transition-colors"
                          >
                            {loadingSub ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <ChevronRight size={12} className={`transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                            )}
                          </button>
                          <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Building2 size={13} className="text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-black truncate">{acct.name}</p>
                            <p className="text-[10px] text-base-content/40 truncate uppercase tracking-wider">{acct.status}</p>
                          </div>
                          {isActiveAccount && !user?.subAccountUID && (
                            <Check size={13} className="text-primary flex-shrink-0" />
                          )}
                        </div>

                        {isOpen && (
                          <div className="bg-base-content/[0.03] border-t border-b border-base-content/5">
                            {subs.length === 0 && !loadingSub ? (
                              <p className="text-[10px] text-base-content/30 px-10 py-2 uppercase tracking-wider font-black">No sub-accounts</p>
                            ) : (
                              subs.map((sub) => {
                                const isActiveSub = isActiveAccount && sub.id === user?.subAccountUID;
                                return (
                                  <button
                                    key={sub.id}
                                    onClick={(e) => handleSwitchSubAccount(acct.id, sub.id, e)}
                                    className={`flex items-center gap-2 w-full px-4 py-2 pl-10 text-left hover:bg-base-content/5 transition-colors ${isActiveSub ? CLASS_TEXT_PRIMARY : 'text-base-content/60'}`}
                                  >
                                    <LayoutGrid size={12} className="flex-shrink-0 opacity-50" />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-black truncate">{sub.name}</p>
                                    </div>
                                    {isActiveSub && <Check size={12} className="text-primary flex-shrink-0" />}
                                  </button>
                                );
                              })
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </>
            )}

            {/* ── ACCOUNT ADMIN: one-level (sub-accounts only) ── */}
            {isAccountAdmin && (
              <>
                {/* All Sub-accounts — reset to account level */}
                <button
                  onClick={handleResetContext}
                  className={`flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-base-content/5 transition-colors border-b border-base-content/5 ${!user?.subAccountUID ? CLASS_TEXT_PRIMARY : CLASS_TEXT_MUTED}`}
                >
                  <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Globe size={13} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-black truncate">All Sub-accounts</p>
                    <p className="text-[10px] text-base-content/40 truncate uppercase tracking-wider">Account view</p>
                  </div>
                  {!user?.subAccountUID && <Check size={13} className="text-primary flex-shrink-0" />}
                </button>

                {loadingOwnSubs ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 size={20} className="animate-spin text-primary" />
                  </div>
                ) : ownSubAccounts.length === 0 ? (
                  <p className="text-base-content/40 text-xs text-center py-6 font-black uppercase">No sub-accounts found</p>
                ) : (
                  ownSubAccounts.map((sub) => {
                    const isActive = sub.id === user?.subAccountUID;
                    return (
                      <button
                        key={sub.id}
                        onClick={() => handleSwitchSubAccount(user.accountUID, sub.id)}
                        className={`flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-base-content/5 transition-colors ${isActive ? CLASS_TEXT_PRIMARY : CLASS_TEXT_MUTED}`}
                      >
                        <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <LayoutGrid size={13} className="text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-black truncate">{sub.name}</p>
                          <p className="text-[10px] text-base-content/40 truncate uppercase tracking-wider">{sub.status}</p>
                        </div>
                        {isActive && <Check size={13} className="text-primary flex-shrink-0" />}
                      </button>
                    );
                  })
                )}
              </>
            )}

          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSwitcher;

AccountSwitcher.propTypes = {
  isCollapsed: PropTypes.bool.isRequired,
};
